import { useMemo, useState } from "react";
import { Button, Loading, Modal, Typography } from "@/components/atoms";
import { DataTable, NoSearchResults, Pagination } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { usePagination } from "@/hooks";
import { useRetainDriverMutation } from "@/redux/apis/driver/driverApi";
import { FleetDriver } from "@/redux/slices/driver/types";
import { Page } from "@/redux/types";
import { addToast } from "@/utils";
import { ArchivedDriversEmptyState } from "./components/ArchivedDriversEmptyState";
import { archivedDriversColumns } from "./fixtures";
import { generateArchivedDriversTableData } from "./helpers";
import { useGetArchivedDriversParams } from "./hooks/useGetArchivedDriversParams";

type UndoModal = { type: "termination" | "driver-left"; driverId: string } | null;

interface ArchivedDriversTableProps {
  items: FleetDriver[];
  page?: Page;
}

export const ArchivedDriversTable = ({ items }: ArchivedDriversTableProps) => {
  const { sorting, setSorting, search, filters } = useGetArchivedDriversParams();
  const [retainDriver, { isLoading }] = useRetainDriverMutation();
  const isFiltersEmpty = Object.values(filters).every((el) => el === undefined);
  const [undoModal, setUndoModal] = useState<UndoModal>(null);

  const handleRetain = () => {
    if (!undoModal) return;
    retainDriver(undoModal.driverId)
      .unwrap()
      .then(() => {
        addToast("success", undoModal.type === "termination" ? "Termination undone successfully." : "Driver has left undone successfully.");
        setUndoModal(null);
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const data = useMemo(
    () =>
      generateArchivedDriversTableData(
        items,
        (driverId) => setUndoModal({ type: "termination", driverId }),
        (driverId) => setUndoModal({ type: "driver-left", driverId })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );

  const renderEmptyState = () => {
    if (search) return <NoSearchResults />;
    if (!isFiltersEmpty) return <ArchivedDriversEmptyState hasFilters />;

    return <ArchivedDriversEmptyState />;
  };

  const isTermination = undoModal?.type === "termination";

  return (
    <div className="relative">
      <DataTable
        className="w-full overflow-x-auto [&_table]:table-auto [&_tr]:bg-white"
        data={data}
        columns={archivedDriversColumns}
        sorting={sorting}
        setSorting={setSorting}
        emptyState={renderEmptyState()}
      />
      <Modal
        open={!!undoModal}
        onClose={() => (isLoading ? null : setUndoModal(null))}
        title={isTermination ? "Undo Termination" : "Undo Driver Has Left"}
        variant="web-small"
        className="relative w-[85vw] md:w-[400px] lg:w-[400px]"
      >
        {isLoading && <Loading />}
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          {isTermination
            ? "Undoing this termination will move the driver back to your "
            : "Undoing this action will move the driver back to your "}
          <span className="font-semibold">My Drivers tab</span> as <span className="font-semibold">Pending</span>.
        </Typography>
        <Typography variant="paragraph" className="mt-3 text-neutral-dark-gray">
          They&apos;ll need to go through the onboarding steps again, and you&apos;ll need to review and approve them before they can regain
          access.
        </Typography>
        <div className="mt-8 flex flex-row items-center justify-end gap-3">
          <Button variant="secondary" size="md" onClick={() => setUndoModal(null)} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleRetain} disabled={isLoading}>
            {isTermination ? "Undo Termination" : "Undo Driver Has Left"}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const ArchivedDriversTablePagination = ({ page }: { page: Page }) => {
  const { handlePageClick, handleSizeChange } = usePagination("archived-drivers");

  return (
    <Pagination
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={page.current}
      pageSize={page.size}
      totalItems={page.total}
      pageCount={page.count}
    />
  );
};

ArchivedDriversTable.Pagination = ArchivedDriversTablePagination;
